<template>
  <div class="edume-widget-wrapper">
    <div class="edume-widget-header">
      <WidgetHeader>
        <template #left>
          <WidgetTitle>{{ widgetTitle }}</WidgetTitle>
        </template>
        <template #right>
          <Alert v-if="isError" />
        </template>
      </WidgetHeader>
    </div>
    <div v-if="isError" class="edume-text">
      <Message :message="errorMessage" />
    </div>
    <div v-else class="edume-container">
      <div v-if="isDataLoaded">
        <div v-if="hasContents">
          <ContentSection
            v-for="section of sections"
            :key="section.id"
            :content="section.content"
            :heading="section.title"
            :tenant-url="tenantUrl"
            :is-desktop="isDesktop"
            :parent-group-id="parentGroupId"
          />
        </div>
        <div v-else>
          <CompletedAllContentMessage :num-items="contentCounts.allContent" />
        </div>
        <div>
          <MoreContent
            :content-counts="contentCounts"
            :sections="sections"
            :dashboard-url="dashboardUrl"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {WidgetHeader, WidgetTitle} from '@beekeeper/home-screen-ui';
import BeekeeperHomeScreen from '@beekeeper/home-screen-sdk';
import {getContentsForUser} from '../services/userService';
import {getAuthUrl} from '../utils/utils';
import {setPreferredLanguage, polyglot} from '../utils/polyglot';

import MoreContent from './MoreContent.vue';
import Message from './Message.vue';
import Alert from './svg/Alert.vue';
import ContentSection from './ContentSection.vue';
import CompletedAllContentMessage from './CompletedAllContentMessage.vue';

// This is the primary app colour defined by the client admin
export const {color: appColour} = BeekeeperHomeScreen.bridge.ui.styling;
export const isDesktop = BeekeeperHomeScreen.bridge.sdk.platform === 'app-web';

export default {
  name: 'EduMe',
  components: {
    WidgetHeader,
    WidgetTitle,
    Message,
    Alert,
    MoreContent,
    ContentSection,
    CompletedAllContentMessage,
  },
  props: {
    homeScreenVisible: {
      type: Boolean,
    },
  },
  data() {
    // Fallback values
    return {
      tenantUrl: null,
      appColour,
      isDesktop,
      isError: false,
      sections: [],
      teamIds: [],
      parentGroupId: null,
      contentCounts: {},
      widgetTitle: '',
      isDataLoaded: false,
    };
  },
  computed: {
    dashboardUrl() {
      return getAuthUrl({
        tenantUrl: this.tenantUrl,
        isDesktop: this.isDesktop,
        contentType: null,
        contentId: null,
        teamIds: this.teamIds,
        parentGroupId: this.parentGroupId,
      });
    },
    hasContents() {
      return this.sections.length > 0;
    },
  },
  // This is the equivalent of useEffect(() => {}, [homeScreenVisible]) in React
  watch: {
    async homeScreenVisible(visible) {
      if (visible) {
        await this.getData();
      }
    },
  },
  // This is the equivalent of useEffect(() => {}, []) in React
  async mounted() {
    await this.getData();
    this.$emit('widget-loaded');
  },
  methods: {
    async getData() {
      try {
        const userDetails = await BeekeeperHomeScreen.sdk.getMe();
        const accountLanguage = userDetails.language.toLowerCase();
        setPreferredLanguage(accountLanguage);

        const userId = userDetails.id.value;
        const tenantId = userDetails.tenant_id.value;

        const {
          tenantUrl,
          widgetTitle,
          sections = [],
          contentCounts = {},
          teamIds = [],
          parentGroupId,
        } = await getContentsForUser(userId, tenantId);

        this.tenantUrl = tenantUrl;
        this.widgetTitle = widgetTitle;
        this.sections = sections;
        this.contentCounts = contentCounts;
        this.teamIds = teamIds;
        this.parentGroupId = parentGroupId;
        this.isError = false;
        this.noCourseMessage = polyglot.t('Message.noCourse');
        this.isDataLoaded = true;
      } catch (error) {
        console.error(error);
        this.errorMessage = polyglot.t('Message.error');
        this.isError = true;
      }
    },
  },
};
</script>

<style scoped>
/* RESETS - scoped to widget only */

/* .edume-widget-wrapper
  *:where(:not(iframe, canvas, img, svg, video):not(svg *, symbol *)) {
  all: unset;
  display: revert;
} */

.edume-widget-wrapper *,
.edume-widget-wrapper *::before,
.edume-widget-wrapper *::after {
  box-sizing: border-box;
  font-family:
    'Inter', 'Source Sans Pro', '-apple-system', 'BlinkMacSystemFont',
    'Segoe UI', 'Roboto', 'Helvetica Neue', arial, sans-serif;
}

.edume-widget-wrapper ol,
.edume-widget-wrapper ul {
  list-style: none;
}

.edume-widget-wrapper table {
  border-collapse: collapse;
}

.edume-widget-wrapper textarea {
  white-space: revert;
}

.edume-widget-wrapper :where([contenteditable]) {
  -moz-user-modify: read-write;
  -webkit-user-modify: read-write;
  overflow-wrap: break-word;
  -webkit-line-break: after-white-space;
}
/* Existing widget-specific styles */
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.edume-widget-header {
  align-items: end !important;
}
.edume-widget-header > div {
  padding-bottom: 8px !important;
}

.edume-widget-header {
  font: var(--widget-title-font) !important;
  color: var(--widget-title-color) !important;
}

.edume-text {
  padding: 0 16px;
  font-size: 16px;
  line-height: 20px;
  color: #5e5f61;
}

.edume-container {
  padding: 0px 16px;
}
</style>
